import { useEffect, useId, useState } from 'react'
import { Moon, Save, Sun } from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/datetime'
import { decimalInputToMinor, formatCurrency, minorToDecimalInput } from '@/lib/format'
import type { AreaGroup, LatePricing, LatePricingInput } from '@/types/api'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { describeLateWindow } from './describe'
import styles from './AreaPricingPage.module.css'

interface LatePricingCardProps {
  latePricing: LatePricing
  /** Whether Cairo is priced by sub-area: late-night pricing waits for it. */
  areaPricingOn: boolean
  groups: AreaGroup[]
  disabled: boolean
  saving: boolean
  /** Asks to switch late-night pricing on or off. Nothing changes until the caller has confirmed it. */
  onChoose: (enabled: boolean) => void
  /** Saves the window and the two prices, leaving the switch as it is. */
  onSave: (input: LatePricingInput) => void
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/
const MIN_MULTIPLIER_HUNDREDTHS = 100
const MAX_MULTIPLIER_HUNDREDTHS = 500
const MAX_FEE_MINOR = 10_000_000 // 100,000 EGP, the server's ceiling

/**
 * Late-night pricing: whether late Cairo trips pay more, when a trip end is
 * late, and what a late end costs.
 *
 * Each end of a trip is judged on its own time — the pickup when the trip
 * starts, the drop-off when it ends, the start plus the package's hours. A late
 * end pays the primary fee in the primary group, and its group's surcharge
 * times the multiplier in any other.
 *
 * The switch works like the pricing mode above it: the radios always show what
 * the server says, and a pick only asks the page to confirm. The window and the
 * prices are a form of their own, saved without touching the switch, so they
 * can be set and checked — in the price table and the trip check — before
 * customers see them.
 */
export function LatePricingCard({
  latePricing,
  areaPricingOn,
  groups,
  disabled,
  saving,
  onChoose,
  onSave,
}: LatePricingCardProps) {
  const { enabled, active } = latePricing
  const formId = useId()
  const [start, setStart] = useState(latePricing.start)
  const [end, setEnd] = useState(latePricing.end)
  const [multiplier, setMultiplier] = useState(latePricing.multiplier.toFixed(2))
  const [primaryFee, setPrimaryFee] = useState(minorToDecimalInput(latePricing.primaryFeeMinor))

  // A save, or a change made by another supervisor, resets the form to what
  // the server now holds.
  useEffect(() => {
    setStart(latePricing.start)
    setEnd(latePricing.end)
    setMultiplier(latePricing.multiplier.toFixed(2))
    setPrimaryFee(minorToDecimalInput(latePricing.primaryFeeMinor))
  }, [latePricing.start, latePricing.end, latePricing.multiplier, latePricing.primaryFeeMinor])

  const multiplierHundredths = decimalInputToMinor(multiplier)
  const primaryFeeMinor = decimalInputToMinor(primaryFee)

  const startError = TIME.test(start) ? undefined : 'Choose a time.'
  const endError = !TIME.test(end)
    ? 'Choose a time.'
    : end === start
      ? 'The window has to end at a different time from when it starts.'
      : undefined
  const multiplierError =
    multiplierHundredths === null
      ? 'Enter a multiplier like 1.25.'
      : multiplierHundredths < MIN_MULTIPLIER_HUNDREDTHS
        ? 'At least 1 — a late end never costs less than by day.'
        : multiplierHundredths > MAX_MULTIPLIER_HUNDREDTHS
          ? 'The multiplier must be 5 or less.'
          : undefined
  const primaryFeeError =
    primaryFeeMinor === null
      ? 'Enter an amount like 80 or 80.50.'
      : primaryFeeMinor > MAX_FEE_MINOR
        ? 'The late fee must be 100,000 or less.'
        : undefined

  const changed =
    start !== latePricing.start ||
    end !== latePricing.end ||
    multiplierHundredths !== Math.round(latePricing.multiplier * 100) ||
    primaryFeeMinor !== latePricing.primaryFeeMinor
  const valid = !startError && !endError && !multiplierError && !primaryFeeError
  const canSave = changed && valid && !disabled && !saving

  const primary = groups.find((group) => group.primary) ?? null

  return (
    <Card className={styles.modeCard}>
      <fieldset className={styles.modeFieldset} disabled={disabled}>
        <legend className={styles.modeLegend}>Late-night pricing</legend>
        <div className={styles.modeOptions}>
          <label className={cn(styles.modeOption, !enabled && styles.modeOptionChosen)}>
            <input type="radio" name="late-night-pricing" checked={!enabled} onChange={() => onChoose(false)} />
            <Sun aria-hidden="true" className={styles.modeIcon} />
            <span className={styles.modeText}>
              <strong>Same price day and night</strong>
              <span>A late Cairo trip costs what the same trip costs by day.</span>
            </span>
          </label>
          <label className={cn(styles.modeOption, enabled && styles.modeOptionChosen)}>
            <input type="radio" name="late-night-pricing" checked={enabled} onChange={() => onChoose(true)} />
            <Moon aria-hidden="true" className={styles.modeIcon} />
            <span className={styles.modeText}>
              <strong>Late trips pay more</strong>
              <span>
                An end of a Cairo trip that falls {describeLateWindow(latePricing)} pays its late price. Customers see
                one final price with it included, once they pick a time.
              </span>
            </span>
          </label>
        </div>

        {enabled && !areaPricingOn ? (
          <p className={styles.lateWaiting}>
            Waiting for sub-area pricing: while Cairo is priced as one area, late trips cost the same as daytime ones.
          </p>
        ) : null}

        {latePricing.updatedAt ? (
          <p className={styles.modeChanged}>
            Set to {enabled ? 'late trips pay more' : 'the same price day and night'} {formatDateTime(latePricing.updatedAt)}
            {latePricing.updatedByAdminName ? ` by ${latePricing.updatedByAdminName}` : ''}
            {enabled && !active ? ', waiting for sub-area pricing' : ''}.
          </p>
        ) : null}
      </fieldset>

      <form
        id={formId}
        className={styles.lateForm}
        aria-label="Late window and prices"
        onSubmit={(event) => {
          event.preventDefault()
          if (!canSave || multiplierHundredths === null || primaryFeeMinor === null) return
          onSave({
            enabled,
            start,
            end,
            multiplier: multiplierHundredths / 100,
            primaryFee: primaryFeeMinor / 100,
          })
        }}
      >
        <div className={styles.lateFields}>
          <Field label="Late from" error={startError} hint="A trip end at this time is late. Cairo time.">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                type="time"
                aria-describedby={describedBy}
                invalid={invalid}
                value={start}
                onChange={(event) => setStart(event.target.value)}
                disabled={disabled || saving}
              />
            )}
          </Field>
          <Field label="Until" error={endError} hint="From this time a trip end is no longer late.">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                type="time"
                aria-describedby={describedBy}
                invalid={invalid}
                value={end}
                onChange={(event) => setEnd(event.target.value)}
                disabled={disabled || saving}
              />
            )}
          </Field>
          <Field
            label="Multiplier"
            error={multiplierError}
            hint="For a late end in any group but the primary one: 80 × 1.25 = 100."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                inputMode="decimal"
                value={multiplier}
                placeholder="1.25"
                addonRight="×"
                onChange={(event) => setMultiplier(event.target.value)}
                disabled={disabled || saving}
              />
            )}
          </Field>
          <Field
            label={`Late fee in ${primary?.name ?? 'the primary group'} (EGP, before VAT)`}
            error={primaryFeeError}
            hint="For a late end in the primary group, which adds nothing by day."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                inputMode="decimal"
                value={primaryFee}
                placeholder="80.00"
                onChange={(event) => setPrimaryFee(event.target.value)}
                disabled={disabled || saving}
              />
            )}
          </Field>
        </div>

        <div className={styles.lateFooter}>
          {groups.length > 0 ? (
            <p className={styles.latePrices}>
              <span className={styles.latePricesLabel}>A late end costs</span>
              {groups.map((group) => (
                <span key={group.id} className={styles.latePrice}>
                  {group.name} <span className={styles.money}>{formatCurrency(group.lateSurcharge)}</span>
                </span>
              ))}
            </p>
          ) : (
            <span />
          )}
          <Button
            type="submit"
            variant="secondary"
            iconLeft={<Save aria-hidden="true" />}
            loading={saving}
            disabled={!canSave}
          >
            Save window and prices
          </Button>
        </div>
      </form>
    </Card>
  )
}
